// „Задържане" по редове: всеки запис в кошницата има свое време — „Сега"
// (влиза веднага), минути или „Ръчно". Чистите правила: какво време има
// записът, какво пише в заглавието и под „Поръчай" и какво пътува с
// `POST /bills` (`kitchenHold` — по група на време).
import type { KitchenHoldIn } from "../../../../api/billKitchenHold";

/** Времето на ред: „now" — влиза веднага (по подразбиране), „manual" — чака,
 *  докато някой не натисне „Освободи", число — влиза сама след толкова минути. */
export type KitchenHoldTime = "now" | "manual" | number;

/** Записът в кошницата носи своето време само когато е избрано за него
 *  (стрелката на реда) и се различава от времето за всички — иначе следва
 *  времето за всички (`defaultTime`). Така новите продукти взимат времето,
 *  избрано отгоре, а изборът за всички пренаписва всеки ред. */
interface HoldableOrderItem {
  kitchenHoldTime?: KitchenHoldTime;
}

/** Времето на записа — неговото или времето за всички. */
export const holdTimeOf = (
  orderItem: HoldableOrderItem,
  defaultTime: KitchenHoldTime,
): KitchenHoldTime => orderItem.kitchenHoldTime ?? defaultTime;

/** Какво пише в заглавието: общото време на всички редове или „mixed";
 *  празна кошница — времето за новите продукти. */
export const commonHoldTimeOf = (
  orderItems: HoldableOrderItem[],
  defaultTime: KitchenHoldTime,
): KitchenHoldTime | "mixed" => {
  const times = new Set(orderItems.map((orderItem) => holdTimeOf(orderItem, defaultTime)));
  if (times.size === 0) return defaultTime;
  return times.size === 1 ? [...times][0] : "mixed";
};

/** Подредбата навсякъде: „Сега", минутите възходящо, „Ръчно" накрая. */
const holdTimeRank = (time: KitchenHoldTime): number =>
  time === "now" ? -1 : time === "manual" ? Number.MAX_SAFE_INTEGER : time;

/** Колко реда с какво време — за надписа под „Поръчай" („1 сега · 3 ръчно").
 *  Празно, когато всичко е „Сега" — тогава под „Поръчай" няма нищо. */
export const holdTimeCountsOf = (
  times: KitchenHoldTime[],
): { time: KitchenHoldTime; count: number }[] => {
  if (times.every((time) => time === "now")) return [];
  const countByTime = new Map<KitchenHoldTime, number>();
  for (const time of times) countByTime.set(time, (countByTime.get(time) ?? 0) + 1);
  return [...countByTime.entries()]
    .map(([time, count]) => ({ time, count }))
    .sort((first, second) => holdTimeRank(first.time) - holdTimeRank(second.time));
};

/**
 * `kitchenHold` за `POST /bills`: по група на всяко време различно от „Сега" —
 * минутите (null = ръчно) и индексите на редовете ѝ в `elements`
 * (`buildBillElements` пази реда на кошницата). BE прави от всяка група
 * отделна задържана партида, а редовете „Сега" тръгват веднага. Групите са
 * подредени като `holdTimeCountsOf` — първата партида е най-скорошната.
 * undefined — всичко е „Сега": тялото е байт за байт старото.
 */
export const kitchenHoldPayloadOf = (
  orderItems: HoldableOrderItem[],
  defaultTime: KitchenHoldTime,
): KitchenHoldIn[] | undefined => {
  const indexesByTime = new Map<Exclude<KitchenHoldTime, "now">, number[]>();
  orderItems.forEach((orderItem, index) => {
    const time = holdTimeOf(orderItem, defaultTime);
    if (time === "now") return;
    indexesByTime.set(time, [...(indexesByTime.get(time) ?? []), index]);
  });
  if (indexesByTime.size === 0) return undefined;
  return [...indexesByTime.entries()]
    .sort(([firstTime], [secondTime]) => holdTimeRank(firstTime) - holdTimeRank(secondTime))
    .map(([time, elementIndexes]) => ({
      minutes: time === "manual" ? null : time,
      elementIndexes,
    }));
};

/** Времето на ред (обобщеният — на всичките му записи). Еднаквото с времето
 *  за всички не се пази — редът пак следва него. */
export const setOrderItemsHoldTime = <T extends HoldableOrderItem>(
  orderItems: T[],
  indices: number[],
  time: KitchenHoldTime,
  defaultTime: KitchenHoldTime,
): T[] =>
  orderItems.map((orderItem, index) =>
    indices.includes(index)
      ? { ...orderItem, kitchenHoldTime: time === defaultTime ? undefined : time }
      : orderItem,
  );

/** Без собствени времена — при избор за всички и при нулиране. Без такива
 *  връща същия масив. */
export const clearOrderItemsHoldTime = <T extends HoldableOrderItem>(orderItems: T[]): T[] =>
  orderItems.some((orderItem) => orderItem.kitchenHoldTime !== undefined)
    ? orderItems.map((orderItem) =>
        orderItem.kitchenHoldTime !== undefined
          ? { ...orderItem, kitchenHoldTime: undefined }
          : orderItem,
      )
    : orderItems;
